import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import Head from 'next/head';
import { IVideoPlayerFields } from '@/types/contentful';
import cx from 'classnames';
import { PlayButton } from './PlayButton';
import { PauseButton } from './PauseButton';
import { MuteButton } from './MuteButton';
import { useInView } from 'react-intersection-observer';
import { useVideo } from '~/hooks/useVideo';
import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { getOptimizedPosterUrl } from '../../utils/contentful';

interface Props extends IVideoPlayerFields {
  sysId?: string;
  inHero?: boolean;
  loop?: boolean;
}

export function NewVideoPlayer({
  sysId,
  title,
  thumbnail,
  video,
  theme,
  startVideoMuted,
  autoplay = false,
  removeBottomPadding,
  inHero,
  loop
}: Readonly<Props>) {
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [hasBeenPlayed, setHasBeenPlayed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Autoplay requires muted — browsers block autoplay on unmuted videos.
  const [isMuted, setIsMuted] = useState(autoplay ? true : (startVideoMuted ?? false));
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  // 
  const videoRefCallback = useCallback((el: HTMLVideoElement | null) => {
    videoElementRef.current = el;
  }, []);
  const [containerRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.75
  });

  const { sourceUrl } = useVideo(video?.fields?.file?.url, {
    manageVisibility: true,
    shouldPlay: isPlayingVideo,
    intersectionThreshold: 0.25,
    deferUntilVisible: true
  });

  useEffect(() => {
    if (inView) setIsVisible(true);
  }, [inView]);

  useEffect(() => {
    if (videoElementRef.current) {
      videoElementRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useLayoutEffect(() => {
    const videoEl = videoElementRef.current;
    if (!videoEl) return;

    const onPlay = () => { setIsPlayingVideo(true); setHasBeenPlayed(true); };
    const onPause = () => setIsPlayingVideo(false);

    videoEl.addEventListener('play', onPlay);
    videoEl.addEventListener('pause', onPause);

    if (autoplay) {
      videoEl.muted = true;
      if (!videoEl.paused) {
        setIsPlayingVideo(true);
        setHasBeenPlayed(true);
      } else {
        videoEl.play()?.catch(() => {
          // Autoplay blocked by browser — PlayButton remains visible
        });
      }
    }

    return () => {
      videoEl.removeEventListener('play', onPlay);
      videoEl.removeEventListener('pause', onPause);
    };
  }, []);

  const handlePlay = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasBeenPlayed) {
      trackWithFingerprint('CTA Clicked', {
        page_url: window.location.href,
        module: inHero ? 'hero' : 'video player'
      });
    }
    videoElementRef.current?.play();
    e.currentTarget.blur();
  };

  const handlePause = (e: React.MouseEvent<HTMLButtonElement>) => {
    videoElementRef.current?.pause();
    e.currentTarget.blur();
  };

  const handleMuteToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (videoElementRef.current) {
      videoElementRef.current.muted = newMuted;
    }
    e.currentTarget.blur();
  };

  const posterUrl = thumbnail ? getOptimizedPosterUrl(thumbnail) : '';

  return (
    <div
      className={cx(
        'block-container layout-padding',
        removeBottomPadding ? 'py-0' : 'py-16',
        inHero ? '!p-0' : ''
      )}
      ref={containerRef}
      id={sysId}
    >
      {inHero && posterUrl && (
        <Head>
          <link
            rel="preload"
            as="image"
            href={posterUrl}
            {...({
              fetchPriority: 'high'
            } as React.LinkHTMLAttributes<HTMLLinkElement>)}
          />
        </Head>
      )}
      <div className="grid grid-cols-12">
        <div
          className={cx(
            'relative z-[2] col-span-12 w-full overflow-hidden rounded-2xl bg-[#023E3B]',
            inHero ? '' : 'desktop:col-span-10 desktop:col-start-2'
          )}
        >
          {isPlayingVideo ? (
            <>
              <div className="group absolute z-[2] flex h-full w-full items-center justify-center">
                <PauseButton
                  theme={theme}
                  className="hidden group-hover:block"
                  onClick={handlePause}
                />
              </div>
              <div className="absolute bottom-4 right-4 z-[3]">
                <MuteButton
                  theme={theme}
                  isMuted={isMuted}
                  onClick={handleMuteToggle}
                />
              </div>
            </>
          ) : (
            <div className="absolute z-[2] flex h-full w-full items-center justify-center bg-black/10 transition-all duration-300">
              <div className="flex flex-col items-center space-y-4">
                <PlayButton
                  theme={theme}
                  className={cx(
                    isVisible ? 'block' : 'hidden',
                    !hasBeenPlayed && isVisible && 'animate-fade-slide-in-up'
                  )}
                  onClick={handlePlay}
                />
                {!hasBeenPlayed && isVisible && (
                  <h2 className="animate-fade-scale-in text-display-3 text-white">
                    {title}
                  </h2>
                )}
              </div>
            </div>
          )}
          <video
            ref={videoRefCallback}
            autoPlay={autoplay}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            poster={posterUrl || undefined}
            playsInline
            muted={isMuted}
            controls={false}
            preload="metadata"
            crossOrigin="anonymous"
            loop={loop}
          >
            <track kind="captions" />
            <source src={sourceUrl} />
            Your browser does not support HTML video.
          </video>
        </div>
      </div>
    </div>
  );
}
